import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';
import clsx from 'clsx';
import { isEmpty } from '@dxb-web-ui-framework/common/utils/Helper';
import { Box, Table, makeStyles } from '@dxb-web-ui-framework/design-system';
import React, { FC } from 'react';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { LabelValuesList } from '../LabelValuesList';
import { LabelValueListItemProps, TableGridConfig } from '../types';
import { TableGridBody } from './TableGridBody/TableGridBody';
import { TableGridFooter } from './TableGridFooter/TableGridFooter';
import { TableGridHeader } from './TableGridHeader/TableGridHeader';

export interface TableGridProps extends TableGridConfig {
  rowData?: Nullable<GenericObject[]>;
  hasSkeleton?: boolean;
  skeletonsToDisplay?: number;
}

const useStyles = makeStyles(theme => ({
  tableContainer: {
    overflowX: 'auto',
    width: '100%',
  },
  table: {
    position: 'sticky',
    top: 0,
    width: '100%',
  },
  hasSkeleton: {
    display: 'table',
  },
}));

const TableGrid: FC<TableGridProps> = ({
  gridOptions,
  columnDefs,
  rowData,
  mobileListConfig,
  hasSkeleton,
  skeletonsToDisplay,
  isRowSelected,
  onClickRow,
  isTableRowClickable,
  children,
}) => {
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const shouldRenderList = isMobile && !isEmpty(mobileListConfig) && gridOptions.collapseToList;
  const shouldDisplaySkeleton = Boolean(hasSkeleton);
  const classes = useStyles();
  if (shouldRenderList) {
    return (
      <>
        <LabelValuesList
          data={rowData || []}
          isRowSelected={isRowSelected}
          listItemActions={mobileListConfig?.listItemActions}
          listItemContents={mobileListConfig?.listItemContents as LabelValueListItemProps[]}
          listItemOnSelect={mobileListConfig?.listItemOnSelect && mobileListConfig.listItemOnSelect}
          hasSkeleton={shouldDisplaySkeleton}
        />
        {rowData && rowData.length === 0 && !hasSkeleton && (
          <Box py={3} display={'flex'} justifyContent={'center'}>
            {gridOptions.noResultsMsg}
          </Box>
        )}
        {children && !shouldDisplaySkeleton && (
          <Box py={3} display={'flex'} justifyContent={'center'}>
            {children}
          </Box>
        )}
      </>
    );
  }

  const reduceColSpans = () =>
    columnDefs.reduce((accumulator: any, currentColumnDef) => {
      if (!currentColumnDef.isHidden)
        return (
          accumulator +
          (currentColumnDef.cellProps && currentColumnDef.cellProps.colSpan ? currentColumnDef.cellProps.colSpan : 1)
        );
      return 0;
    }, 0);

  // TODO: consider if should render list should check parent container element dimensions instead of breakpoints

  return (
    <Box className={classes.tableContainer}>
      <Table
        aria-label={gridOptions.ariaTitle}
        classes={{ root: clsx(classes.table, { [classes.hasSkeleton]: shouldDisplaySkeleton }) }}
        padding={gridOptions.padding ? gridOptions.padding : 'normal'}
        size={gridOptions.size ? gridOptions.size : 'medium'}
        stickyHeader={Boolean(gridOptions.stickyHeader)}
        data-testid={gridOptions.dataTestId ? gridOptions.dataTestId : 'TableGrid'}>
        <TableGridHeader columnDefs={columnDefs} />
        <TableGridBody
          hasSkeleton={shouldDisplaySkeleton}
          skeletonsToDisplay={skeletonsToDisplay}
          {...gridOptions}
          rowData={rowData || []}
          columnDefs={columnDefs}
          isRowSelected={isRowSelected}
          isTableRowClickable={isTableRowClickable}
          onClickRow={onClickRow}
          colSpan={reduceColSpans()}
        />
        {children && !shouldDisplaySkeleton && <TableGridFooter colSpan={reduceColSpans()}>{children}</TableGridFooter>}
      </Table>
    </Box>
  );
};

export { TableGrid };
